/**
 * Name-based Light/Dark heuristic for swatch-only colors.
 *
 * When the supplier color has no usable hex (only a swatch_url, or
 * neither), we fall back to the descriptive English COLOR_NAME to make a
 * best-guess classification. SanMar's top 30 color names cover roughly
 * 80% of inventory; matching them with a small dictionary turns most of
 * the previously-"?" defaults into confident labels.
 *
 * Three-tier confidence is returned so the UI can distinguish:
 *   - 'high'  — saturated, unambiguous word (Black, White, Carolina) OR
 *               an explicit "Dark X" / "Light X" / "Deep X" modifier.
 *   - 'low'   — mid-tone word (Heather, Grey, Steel). We default to
 *               'dark' here (industry convention: decorate in white /
 *               light thread on mid-tones) but tag the result so the
 *               UI can show it's a guess.
 *   - 'none'  — no recognized words. Caller renders the ambiguous '?'
 *               pip and lets the user pick manually.
 *
 * Pebble P2.4 / task #167. Refines the always-render pip from PR #1138.
 */

// LIGHT words — saturated light colors and pale tones.
const LIGHT_WORDS = new Set([
    'white', 'cream', 'ivory', 'natural', 'pearl', 'silver',
    'platinum', 'khaki', 'tan', 'sand', 'beige', 'ash',
    'mint', 'lemon', 'lime', 'yellow', 'aqua', 'sky',
    'pale', 'pastel', 'soft', 'carolina',
]);

// DARK words — saturated darks + mid-saturation colors that decoration
// treats as dark (you'd embroider in white / light thread).
const DARK_WORDS = new Set([
    'black', 'navy', 'maroon', 'forest', 'olive', 'army',
    'charcoal', 'graphite', 'midnight', 'royal', 'indigo',
    'crimson', 'burgundy', 'plum', 'chocolate', 'espresso',
    'jet', 'blacktop',
    // saturated mid-tones default dark:
    'red', 'blue', 'green', 'purple', 'orange', 'pink',
    'brown', 'gold', 'rust', 'wine', 'teal',
]);

// Mid-tones — usually grey-ish. Default to dark (industry convention)
// but tagged low-confidence so the UI can show it's a guess.
const AMBIGUOUS_WORDS = new Set([
    'grey', 'gray', 'heather', 'heathered', 'storm', 'steel',
    'slate', 'iron', 'stone', 'pewter', 'smoke', 'mist',
]);

/**
 * Classify a color name into { ld, confidence }.
 *
 * @param {string} name  The color's descriptive name (SanMar COLOR_NAME).
 * @returns {{ ld: 'light' | 'dark' | null, confidence: 'high' | 'low' | 'none' }}
 */
export function classifyByName(name) {
    if (typeof name !== 'string' || name.trim() === '') {
        return { ld: null, confidence: 'none' };
    }
    // Split on whitespace, slash, hyphen, underscore. Treat & like "and".
    const words = name
        .toLowerCase()
        .replace(/&/g, ' and ')
        .split(/[\s/_\-]+/)
        .filter(Boolean);

    if (words.length === 0) {
        return { ld: null, confidence: 'none' };
    }

    // Modifiers — "Dark X" / "Light X" / "Deep X" override the base word.
    if (words.includes('dark') || words.includes('deep')) {
        return { ld: 'dark', confidence: 'high' };
    }
    if (words.includes('light')) {
        return { ld: 'light', confidence: 'high' };
    }

    // Direct dictionary hit (first match wins; scan LIGHT before DARK so
    // mixed-token names like "Carolina Blue" route via the more-specific
    // LIGHT word rather than the generic "Blue").
    for (const w of words) {
        if (LIGHT_WORDS.has(w)) return { ld: 'light', confidence: 'high' };
        if (DARK_WORDS.has(w)) return { ld: 'dark', confidence: 'high' };
    }

    // Mid-tone words — low confidence guess (default dark).
    for (const w of words) {
        if (AMBIGUOUS_WORDS.has(w)) {
            return { ld: 'dark', confidence: 'low' };
        }
    }

    return { ld: null, confidence: 'none' };
}
